import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Steps, message } from 'antd';
import styled from 'styled-components';
import { Box, Text } from 'atoms';
import VendorFormV2 from 'componentsv2/forms/VendorFormV2';
import { createVendorV2, addVendorServicabilityRouteV2 } from 'store/vendorSliceV2';
import { createVehicleV2 } from 'store/vehicleSliceV2';
import { transformVehicleToAPIFormat } from './CreateVendorPageV2.utils';

const { Step } = Steps;

const API_ERRORS_LOG_PREFIX = 'API Errors:';

const PageContainer = styled(Box)`
	background: #f4f6f8;
	min-height: 100vh;
	padding: 0;
`;

const HeaderContainer = styled(Box)`
	background: #fff;
	padding: 24px 32px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const PageTitle = styled(Text)`
	font-size: 24px;
	font-weight: 600;
	color: #1c1c1c;
	margin-bottom: 4px;
	display: block;
`;

const PageSubtitle = styled(Text)`
	font-size: 14px;
	color: #707070;
	display: block;
`;

const StepsContainer = styled(Box)`
	background: #fff;
	padding: 32px;
	margin-top: 1px;
`;

const StyledSteps = styled(Steps)`
	.ant-steps-item-process .ant-steps-item-icon {
		background-color: #18405d;
		border-color: #18405d;
	}

	.ant-steps-item-finish .ant-steps-item-icon {
		background-color: #18405d;
		border-color: #18405d;
	}

	.ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon {
		color: #fff;
	}

	.ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-tail::after {
		background-color: #18405d;
	}

	.ant-steps-item-title {
		font-size: 13px;
		color: #8c8c8c;
	}

	.ant-steps-item-process .ant-steps-item-title {
		color: #1c1c1c;
		font-weight: 500;
	}

	.ant-steps-item-finish .ant-steps-item-title {
		color: #1c1c1c;
	}

	/* Disable future steps - make them unclickable and visually disabled */
	.ant-steps-item-wait.step-disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}

	.ant-steps-item-wait.step-disabled .ant-steps-item-container {
		cursor: not-allowed;
		pointer-events: none;
	}

	.ant-steps-item-wait.step-disabled .ant-steps-item-icon {
		cursor: not-allowed;
	}

	.ant-steps-item-wait.step-disabled .ant-steps-item-title {
		cursor: not-allowed;
		color: #d9d9d9;
	}
`;

const ContentContainer = styled(Box)`
	background: #fff;
	margin: 0 32px;
	margin-top: 1px;
	padding: 32px;
	min-height: calc(100vh - 250px);
`;

const STEPS = [
	{
		title: 'Basic Information',
		key: 'basic_information',
	},
	{
		title: 'Business Information',
		key: 'business_information',
	},
	{
		title: 'Banking Information',
		key: 'banking_information',
	},
	{
		title: 'Vehicle Information',
		key: 'vehicle_information',
	},
	{
		title: 'Serviceability Info',
		key: 'serviceability_info',
	},
];

export default function CreateVendorPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [currentStep, setCurrentStep] = useState(0);
	const [submitting] = useState(false); // Keep for backward compatibility with VendorFormV2
	const [maxStepReached, setMaxStepReached] = useState(0); // Track the highest step user has reached
	const [savedVendorId, setSavedVendorId] = useState(null); // Track if vendor has been created
	const [saving, setSaving] = useState(false); // Track save operation in progress
	const [vehiclesSaved, setVehiclesSaved] = useState(false); // Track if vehicles have been saved
	const [routesSaved, setRoutesSaved] = useState(false); // Track if routes have been saved

	const handleNext = () => {
		if (currentStep < STEPS.length - 1) {
			const nextStep = currentStep + 1;
			setCurrentStep(nextStep);
			// Update the maximum step reached
			if (nextStep > maxStepReached) {
				setMaxStepReached(nextStep);
			}
		}
	};

	const handlePrevious = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		}
	};

	const handleStepClick = (step) => {
		// Only allow clicking on steps that have been reached before
		// Users can go back to previous steps, but not forward to unvisited steps
		if (step <= maxStepReached) {
			setCurrentStep(step);
		}
	};

	/**
	 * Validate required fields
	 */
	const validateRequiredFields = (formValues) => {
		// Base required fields (always required)
		const baseRequiredFields = [
			'vendor_name',
			'vendor_type',
			'legal_structure',
			'vendor_email_id',
			'primary_poc_name',
			'primary_poc_contact',
			'pincode',
			'city',
			'state',
			'pan_file_key',
			'pan_number',
			'gst_type',
			'aadhar_file_key',
			'aadhar_number',
			'account_holder_name',
			'account_number',
			'bank_name',
			'ifsc_code',
			'branch_name',
			'bank_document_key',
			'vrf_document_key',
		];

		// Conditionally required fields
		const conditionalFields = [];

		// GST fields are only required if gst_type is not "Unregistered"
		if (formValues.gst_type && formValues.gst_type !== 'Unregistered') {
			conditionalFields.push('gst_file_key', 'gst_number');
		}

		// Combine all required fields
		const requiredFields = [...baseRequiredFields, ...conditionalFields];

		const missingFields = requiredFields.filter(
			(field) => !formValues[field] && formValues[field] !== 0 && formValues[field] !== false
		);

		console.log('🔍 [validateRequiredFields] Checking required fields:', {
			gstType: formValues.gst_type,
			totalRequired: requiredFields.length,
			missingCount: missingFields.length,
			missingFields,
		});

		if (missingFields.length > 0) {
			console.log('❌ [validateRequiredFields] Missing fields:', missingFields);
			message.error(
				`Please fill in all required fields. Missing: ${missingFields.join(', ')}`
			);
			return false;
		}
		console.log('✅ [validateRequiredFields] All required fields present');
		return true;
	};

	/**
	 * Validate MSME fields and VRF document (VRF is independent of MSME)
	 */
	const validateMSMEFields = (formValues) => {
		// VRF document is always required
		if (!formValues.vrf_document_key) {
			message.error('Please upload the VRF document');
			return false;
		}

		// MSME number is required only when vendor is registered as MSME
		if (formValues.vendor_registered_msme && !formValues.msme_number) {
			message.error('MSME Number is required when vendor is registered as MSME');
			return false;
		}

		return true;
	};

	/**
	 * Get document type from file extension
	 */
	const getDocumentType = (fileKey) => {
		if (!fileKey) return 'PDF';
		const ext = fileKey.split('.').pop()?.toLowerCase();
		if (ext === 'jpg' || ext === 'jpeg') return 'JPG';
		if (ext === 'png') return 'PNG';
		return 'PDF';
	};

	/**
	 * Build document_info object from form values
	 */
	const buildDocumentInfo = (formValues) => {
		if (!formValues || typeof formValues !== 'object') {
			return {};
		}

		const document_info = {};

		// Only add PAN if both file and number exist
		if (formValues.pan_file_key && formValues.pan_number) {
			document_info.pan = {
				document_key: formValues.pan_file_key,
				document_type: getDocumentType(formValues.pan_file_key),
				number: formValues.pan_number,
			};
		}

		// Handle GST based on gst_type
		if (formValues.gst_type) {
			const gstTypeMap = {
				Registered: 'Registered',
				Composition: 'Composition',
				Unregistered: 'Unregistered',
			};
			const gstType = gstTypeMap[formValues.gst_type] || formValues.gst_type;

			// If Unregistered, only include gst_type
			if (formValues.gst_type === 'Unregistered') {
				document_info.gst = {
					gst_type: gstType,
				};
			}
			// If Registered or Composition, include all GST fields only if both file and number exist
			else if (formValues.gst_file_key && formValues.gst_number) {
				document_info.gst = {
					document_key: formValues.gst_file_key,
					document_type: getDocumentType(formValues.gst_file_key),
					gst_type: gstType,
					number: formValues.gst_number,
				};
			}
			// If Registered/Composition but missing fields, don't add incomplete GST object
			// Validation will catch this at the form level
		}

		// Only add Aadhar if both file and number exist
		if (formValues.aadhar_file_key && formValues.aadhar_number) {
			document_info.aadhar = {
				document_key: formValues.aadhar_file_key,
				document_type: getDocumentType(formValues.aadhar_file_key),
				number: formValues.aadhar_number,
			};
		}

		// VRF/UDF document is independent of MSME
		if (formValues.vrf_document_key) {
			document_info.udf = {
				document_key: formValues.vrf_document_key,
			};
		}

		return document_info;
	};

	/**
	 * Build banking_info object from form values
	 */
	const buildBankingInfo = (formValues) => {
		if (!formValues || typeof formValues !== 'object') {
			return {
				bank_address: {},
				document_info: {},
			};
		}

		// Convert tds_applicable from 'Yes'/'No' string to boolean
		const tdsApplicable =
			formValues.tds_applicable === true ||
			formValues.tds_applicable === 'Yes' ||
			formValues.tds_applicable === 'yes';

		const banking_info = {
			bank_name: formValues.bank_name || '',
			account_number: formValues.account_number || '',
			ifsc_code: formValues.ifsc_code || '',
			account_holder_name: formValues.account_holder_name || '',
			branch_name: formValues.branch_name || '',
			account_type: formValues.account_type || 'current', // Default to 'current' for business accounts
			tds_applicable: tdsApplicable,
			bank_address: {
				address_line1: formValues.vendor_address || '',
				address_line2: '',
				pincode: formValues.pincode || '',
				city: formValues.city || '',
				state: formValues.state || '',
			},
			document_info: {},
		};

		if (formValues.bank_document_key) {
			banking_info.document_info.branch_document = {
				document_key: formValues.bank_document_key,
			};
		}

		return banking_info;
	};

	/**
	 * Build vendor address object
	 */
	const buildVendorAddress = (formValues) => {
		const address = {
			address_line: formValues.vendor_address || '',
			pincode: formValues.pincode || '',
			state: formValues.state || '',
			city: formValues.city || '',
		};
		if (formValues.zone) {
			address.zone = formValues.zone;
		}
		return address;
	};

	/**
	 * Build optional contact fields
	 */
	const buildOptionalContacts = (formValues) => {
		const contacts = {};
		if (formValues.alternative_poc_name) {
			contacts.alternative_poc_name = formValues.alternative_poc_name;
		}
		if (formValues.alternative_poc_contact) {
			contacts.alternative_poc_contact = formValues.alternative_poc_contact;
		}
		return contacts;
	};

	/**
	 * Build MSME fields if applicable
	 */
	const buildMSMEFields = (formValues) => {
		if (formValues.vendor_registered_msme && formValues.msme_number) {
			return { msme_number: formValues.msme_number };
		}
		return {};
	};

	/**
	 * Transform basic vendor information
	 */
	const transformBasicVendorInfo = (formValues) => ({
		vendor_name: formValues.vendor_name || '',
		vendor_type: formValues.vendor_type || '',
		legal_structure: formValues.legal_structure || '',
		vendor_email_id: formValues.vendor_email_id || '',
		primary_poc_name: formValues.primary_poc_name || '',
		primary_poc_contact: formValues.primary_poc_contact || '',
		...buildOptionalContacts(formValues),
		vendor_address: buildVendorAddress(formValues),
		business_unit_id: formValues.business_unit_id || 5,
		fds_applicable: formValues.fds_applicable || false,
		vendor_registered_msme: formValues.vendor_registered_msme || false,
		...buildMSMEFields(formValues),
	});

	/**
	 * Build vehicle data for API
	 */
	const buildVehicleData = (formValues) => {
		if (
			!formValues ||
			!Array.isArray(formValues.vehicles) ||
			formValues.vehicles.length === 0
		) {
			return null;
		}

		// Transform vehicles array to API format
		const vehicles = formValues.vehicles.map((vehicle) => ({
			vehicle_type: vehicle.vehicle_type || vehicle.vehicleType || '',
			vehicle_number: vehicle.vehicle_number || vehicle.vehicleNumber || '',
			rc_document_key: vehicle.rc_document_key || '',
			rc_valid_till: vehicle.rc_valid_till || null,
			insurance_document_key: vehicle.insurance_document_key || '',
			insurance_valid_till: vehicle.insurance_valid_till || null,
			puc_document_key: vehicle.puc_document_key || '',
			puc_valid_till: vehicle.puc_valid_till || null,
			ownership_type: vehicle.ownership_type || vehicle.ownershipType || '',
			consent_letter_key: vehicle.consent_letter_key || '',
		}));

		return vehicles.length > 0 ? vehicles : null;
	};

	/**
	 * Transform form values to API request format
	 */
	const transformFormDataToAPI = (formValues) => {
		if (!formValues || typeof formValues !== 'object') {
			throw new Error('Invalid form values');
		}

		const document_info = buildDocumentInfo(formValues) || {};
		const banking_info = buildBankingInfo(formValues) || {};
		const vehicles = buildVehicleData(formValues);

		const apiData = {
			...transformBasicVendorInfo(formValues),
			document_info,
			banking_info,
		};

		// Add vehicles if present
		if (vehicles && vehicles.length > 0) {
			apiData.vehicles = vehicles;
		}

		return apiData;
	};

	/**
	 * Format error message - handles both string and object error messages
	 */
	const formatErrorMessage = (errorMessage) => {
		if (!errorMessage) {
			return 'Failed to create vendor. Please try again.';
		}

		// If message is an object (validation errors), format it
		if (typeof errorMessage === 'object' && !Array.isArray(errorMessage)) {
			const errorEntries = Object.entries(errorMessage);
			if (errorEntries.length > 0) {
				return errorEntries.map(([field, msg]) => `${field}: ${msg}`).join('; ');
			}
			return 'Validation failed. Please check your input.';
		}

		// If message is already a string, return it
		return String(errorMessage);
	};

	/**
	 * Helper: Handle vendor create response
	 */
	const handleVendorCreateResponse = (response) => {
		if (response && response.status === 'success' && response?.data?.id) {
			const vendorId = response.data.id;
			setSavedVendorId(vendorId);
			message.success(response?.message || 'Vendor created successfully');
			return true;
		}
		const errorData = response?.error || response || {};
		const rawErrorMessage =
			errorData?.message ||
			errorData?.details?.message ||
			response?.message ||
			'Failed to create vendor';
		message.error(formatErrorMessage(rawErrorMessage));
		return false;
	};

	/**
	 * Handle Save Vendor (creates vendor but stays on page)
	 * Called from step 2 (Banking Information) only
	 */
	const handleSaveVendor = async (formValues, setErrors) => {
		if (!formValues || typeof formValues !== 'object') {
			message.error('Invalid form data');
			return;
		}

		// Only allow save on step 2 (Banking Information)
		if (currentStep !== 2) {
			message.error('Invalid step for vendor save');
			return;
		}

		setSaving(true);
		try {
			// Validate required fields for current step
			if (!validateRequiredFields(formValues)) {
				setSaving(false);
				return;
			}

			if (!validateMSMEFields(formValues)) {
				setSaving(false);
				return;
			}

			// Transform form data to API format (only Basic/Business/Banking info)
			const apiData = transformFormDataToAPI(formValues);
			const defaultSetErrors = (errors) => console.error(API_ERRORS_LOG_PREFIX, errors);

			// On step 2, always create vendor (first time saving)
			const response = await dispatch(
				createVendorV2({
					req_data: apiData,
					setErrors: setErrors || defaultSetErrors,
				})
			);
			handleVendorCreateResponse(response);
		} catch (error) {
			console.error('Error saving vendor:', error);
			const rawErrorMessage =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				'Failed to save vendor. Please try again.';
			message.error(formatErrorMessage(rawErrorMessage));
		} finally {
			setSaving(false);
		}
	};

	/**
	 * Helper: Extract vehicle type ID from vehicle object
	 */
	const getVehicleTypeId = (vehicle) =>
		vehicle.vehicle_type_id ||
		vehicle.vehicleTypeId ||
		vehicle.vehicle_type ||
		vehicle.vehicleType;

	/**
	 * Helper: Create error result for vehicle save
	 */
	const createVehicleErrorResult = (vehicle, errorMessage) => ({
		success: false,
		vehicleNumber: vehicle.vehicle_number || vehicle.vehicleNumber || 'Unknown',
		error: errorMessage,
	});

	/**
	 * Helper: Extract error message from API response
	 */
	const extractVehicleErrorMessage = (response) =>
		response?.error?.message?.vehicle_type_id ||
		response?.error?.message ||
		response?.message ||
		'Failed to create vehicle';

	/**
	 * Helper: Process single vehicle save
	 */
	const processVehicleSave = async (vehicle, vendorId, setErrors) => {
		try {
			// Validate vehicle_type_id before transformation
			const vehicleTypeId = getVehicleTypeId(vehicle);
			if (!vehicleTypeId) {
				return createVehicleErrorResult(vehicle, 'Vehicle type is required');
			}

			const vehicleData = transformVehicleToAPIFormat(vehicle, vendorId);
			const defaultSetErrors = (errors) => console.error(API_ERRORS_LOG_PREFIX, errors);

			const response = await dispatch(
				createVehicleV2({
					req_data: vehicleData,
					setErrors: setErrors || defaultSetErrors,
				})
			);

			if (response && response.status === 'success') {
				return {
					success: true,
					vehicleNumber: vehicle.vehicle_number || vehicle.vehicleNumber,
					vehicleId: response?.data?.id,
				};
			}

			return createVehicleErrorResult(vehicle, extractVehicleErrorMessage(response));
		} catch (error) {
			// Handle transformation errors (e.g., missing vehicle_type_id)
			if (error.message && error.message.includes('vehicle_type_id')) {
				return createVehicleErrorResult(vehicle, 'Vehicle type is required');
			}

			const errorMsg =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				'Unknown error';
			return createVehicleErrorResult(vehicle, errorMsg);
		}
	};

	/**
	 * Helper: Show vehicle save results
	 */
	const showVehicleSaveResults = (processedResults, totalCount) => {
		const successCount = processedResults.filter((r) => r.success).length;
		const failedCount = totalCount - successCount;

		if (successCount === totalCount) {
			message.success(`All ${successCount} vehicle(s) saved successfully`);
		} else if (successCount > 0) {
			message.warning(`${successCount} vehicle(s) saved, ${failedCount} failed`);
		} else {
			message.error('Failed to save any vehicles. Please check the errors and try again.');
		}
	};

	/**
	 * Handle Save Vehicles (creates vehicles but stays on page)
	 * Called from step 3 (Vehicle Information)
	 */
	const handleSaveVehicles = async (formValues, setErrors, setFieldValue) => {
		if (!savedVendorId) {
			message.warning('Please save the vendor first before saving vehicles');
			return;
		}

		const vehicles = formValues?.vehicles || [];
		if (vehicles.length === 0) {
			message.warning('Please add at least one vehicle before saving');
			return;
		}

		setSaving(true);
		try {
			const vehiclePromises = vehicles.map((vehicle) =>
				processVehicleSave(vehicle, savedVendorId, setErrors)
			);

			const results = await Promise.allSettled(vehiclePromises);
			const processedResults = results.map((result) =>
				result.status === 'fulfilled' ? result.value : { success: false }
			);

			// Update vehicles array with backend IDs
			const updatedVehicles = vehicles.map((vehicle, index) => {
				const result = processedResults[index];
				return result?.success && result?.vehicleId
					? { ...vehicle, id: result.vehicleId }
					: vehicle;
			});

			if (setFieldValue && typeof setFieldValue === 'function') {
				setFieldValue('vehicles', updatedVehicles);
			}

			// Mark vehicles as saved if at least one was successfully saved
			const successCount = processedResults.filter((r) => r.success).length;
			if (successCount > 0) {
				setVehiclesSaved(true);
			}

			showVehicleSaveResults(processedResults, vehicles.length);
		} catch (error) {
			console.error('Error saving vehicles:', error);
			message.error('An unexpected error occurred during vehicle save');
		} finally {
			setSaving(false);
		}
	};

	/**
	 * Helper: Process single route save
	 */
	/**
	 * Helper: Extract route data from route object (handles both camelCase and snake_case)
	 */
	const getRouteData = (route) => ({
		type_of_movement: route.type_of_movement || route.typeOfMovement || '',
		source_city: route.source_city || route.sourceCity || '',
		destination_city: route.destination_city || route.destinationCity || '',
	});

	/**
	 * Helper: Create error result for route save
	 */
	const createRouteErrorResult = (route, errorMessage) => {
		const routeData = getRouteData(route);
		const routeLabel = `${routeData.source_city} → ${routeData.destination_city}`;
		return {
			success: false,
			route: routeLabel || 'Unknown route',
			error: errorMessage,
		};
	};

	/**
	 * Helper: Process single route save
	 */
	const processRouteSave = async (route, vendorId, setErrors) => {
		try {
			const routeData = getRouteData(route);

			// Validate required fields
			if (
				!routeData.type_of_movement ||
				!routeData.source_city ||
				!routeData.destination_city
			) {
				return createRouteErrorResult(
					route,
					'Type of movement, source city, and destination city are required'
				);
			}

			const defaultSetErrors = (errors) => console.error(API_ERRORS_LOG_PREFIX, errors);
			const response = await dispatch(
				addVendorServicabilityRouteV2({
					vendor_id: vendorId,
					type_of_movement: routeData.type_of_movement,
					source_city: routeData.source_city,
					destination_city: routeData.destination_city,
					setErrors: setErrors || defaultSetErrors,
				})
			);

			if (response && response.status === 'success') {
				const routeLabel = `${routeData.source_city} → ${routeData.destination_city}`;
				return {
					success: true,
					route: routeLabel,
					routeId: response?.data?.id,
				};
			}

			const errorMessage =
				response?.error?.message || response?.message || 'Failed to create route';
			return createRouteErrorResult(route, errorMessage);
		} catch (error) {
			const errorMsg =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				'Unknown error';
			return createRouteErrorResult(route, errorMsg);
		}
	};

	/**
	 * Helper: Show route save results
	 */
	const showRouteSaveResults = (processedResults, totalCount) => {
		const successCount = processedResults.filter((r) => r.success).length;
		const failedCount = totalCount - successCount;

		if (successCount === totalCount) {
			message.success(`All ${successCount} route(s) saved successfully`);
		} else if (successCount > 0) {
			message.warning(`${successCount} route(s) saved, ${failedCount} failed`);
		} else {
			message.error('Failed to save any routes. Please check the errors and try again.');
		}
	};

	/**
	 * Handle Save Routes (creates routes but stays on page)
	 * Called from step 4 (Serviceability Info)
	 */
	const handleSaveRoutes = async (formValues, setErrors, setFieldValue) => {
		if (!savedVendorId) {
			message.warning('Please save the vendor first before saving routes');
			return;
		}

		const routes = formValues?.routes || [];
		if (routes.length === 0) {
			message.warning('Please add at least one route before saving');
			return;
		}

		setSaving(true);
		try {
			const routePromises = routes.map((route) =>
				processRouteSave(route, savedVendorId, setErrors)
			);

			const results = await Promise.allSettled(routePromises);
			const processedResults = results.map((result) =>
				result.status === 'fulfilled' ? result.value : { success: false }
			);

			// Update routes array with backend IDs
			const updatedRoutes = routes.map((route, index) => {
				const result = processedResults[index];
				return result?.success && result?.routeId
					? { ...route, id: result.routeId }
					: route;
			});

			if (setFieldValue && typeof setFieldValue === 'function') {
				setFieldValue('routes', updatedRoutes);
			}

			// Mark routes as saved if at least one was successfully saved
			const successCount = processedResults.filter((r) => r.success).length;
			if (successCount > 0) {
				setRoutesSaved(true);
			}

			showRouteSaveResults(processedResults, routes.length);
		} catch (error) {
			console.error('Error saving routes:', error);
			message.error('An unexpected error occurred during route save');
		} finally {
			setSaving(false);
		}
	};

	/**
	 * Handle Save - routes to appropriate save handler based on current step
	 */
	const handleSave = async (formValues, setErrors, setFieldValue) => {
		if (currentStep <= 2) {
			// Steps 0-2: Save vendor
			await handleSaveVendor(formValues, setErrors);
		} else if (currentStep === 3) {
			// Step 3: Save vehicles
			await handleSaveVehicles(formValues, setErrors, setFieldValue);
		} else if (currentStep === 4) {
			// Step 4: Save routes
			await handleSaveRoutes(formValues, setErrors, setFieldValue);
		}
	};

	const handleSubmit = async (formValues) => {
		if (submitting) return; // Prevent multiple submissions

		if (!formValues || typeof formValues !== 'object') {
			message.error('Invalid form data');
			return;
		}

		// Only navigate to vendor list (no API call)
		// All data should already be saved via Save buttons
		if (!savedVendorId) {
			message.error('Please save the vendor first before submitting');
			return;
		}

		// Check if routes need to be saved (on Serviceability Info step)
		if (currentStep === 4) {
			const routes = formValues?.routes || [];
			// If there are routes in the form but they haven't been saved
			if (routes.length > 0 && !routesSaved) {
				message.error('Please click Save to save the routes before submitting');
				return;
			}
		}

		// Navigate to vendor list
		navigate('/vendor-list');
	};

	return (
		<PageContainer>
			<HeaderContainer>
				<PageTitle>Create Vendor</PageTitle>
				<PageSubtitle>Add and onboard new Vendors</PageSubtitle>
			</HeaderContainer>

			<StepsContainer>
				<StyledSteps current={currentStep} onChange={handleStepClick}>
					{STEPS.map((step, index) => (
						<Step
							key={step.key}
							title={step.title}
							className={index > maxStepReached ? 'step-disabled' : ''}
							disabled={index > maxStepReached}
						/>
					))}
				</StyledSteps>
			</StepsContainer>

			<ContentContainer>
				<VendorFormV2
					currentStep={currentStep}
					onNext={handleNext}
					onPrevious={handlePrevious}
					onSubmit={handleSubmit}
					totalSteps={STEPS.length}
					submitting={submitting}
					onSave={handleSave}
					savedVendorId={savedVendorId}
					saving={saving}
					isEditMode={false}
					vehiclesSaved={vehiclesSaved}
					routesSaved={routesSaved}
				/>
			</ContentContainer>
		</PageContainer>
	);
}
